<template>
	<div class="container" id="faq_admin_section">
		<div v-if="user_level < 90">
			<h5 class="text-danger text-center mt-5">Access Denied!</h5>
		</div>

		<div v-else>
			<!-- Page header -->
			<div class="page-header gold mt-4 mb-4">
				<back-btn></back-btn>
				<h2 class="mb-0">
					<i class="fas fa-question-circle pr-2"></i>
					FAQs
				</h2>
			</div>

			<!-- Add button -->
			<div class="text-right mb-3">
				<b-button class="btn-faq-add" size="sm" @click="faqAdd">
					<i class="fas fa-plus mr-1"></i> Add FAQ
				</b-button>
			</div>

			<!-- FAQ modal -->
			<b-modal
				id="modal-faq"
				:title="modal.title"
				ref="modal-faq"
				no-close-on-backdrop
				size="lg"
				header-class="faq-modal-header"
			>
				<b-form @submit.prevent="submitFaqForm()">
					<!-- Question -->
					<b-form-group label-for="faq-question">
						<template #label>
							Question <span class="text-danger">*</span>
						</template>
						<b-form-textarea
							id="faq-question"
							v-model="modal.faq.question"
							rows="2"
							max-rows="4"
							placeholder="Enter the FAQ question…"
							:state="modal.showErrors ? !!modal.faq.question : null"
							@input="validate()"
						></b-form-textarea>
						<b-form-invalid-feedback>Question is required.</b-form-invalid-feedback>
					</b-form-group>

					<!-- Answer -->
					<b-form-group>
						<template #label>
							Answer <span class="text-danger">*</span>
						</template>
						<div :class="['editor-wrap', modal.showErrors && !hasAnswerContent ? 'editor-invalid' : '']">
							<vue-editor
								v-model="modal.faq.answer"
								:editor-toolbar="editorToolbar"
								@text-change="validate()"
								placeholder="Write the answer here…"
							></vue-editor>
						</div>
						<small class="text-danger" v-if="modal.showErrors && !hasAnswerContent">
							Answer is required.
						</small>
					</b-form-group>

					<!-- Status + Sort in one row -->
					<div class="row">
						<div class="col-sm-6">
							<b-form-group label="Status" label-for="faq-status">
								<b-form-select
									id="faq-status"
									v-model="modal.faq.status"
									:options="statuses"
									@change="validate()"
								></b-form-select>
							</b-form-group>
						</div>
						<div class="col-sm-6">
							<b-form-group label="Sort Order" label-for="faq-sort">
								<b-form-input
									id="faq-sort"
									v-model="modal.faq.sort"
									type="number"
									min="0"
									placeholder="e.g. 100"
									@input="validate()"
								></b-form-input>
								<small class="text-muted">Lower numbers appear first.</small>
							</b-form-group>
						</div>
					</div>
				</b-form>

				<!-- Process error -->
				<b-alert
					:show="!!modal.errors.process"
					variant="danger"
					class="mb-0 mt-2 py-2 px-3"
				>{{ modal.errors.process }}</b-alert>

				<template #modal-footer="{ cancel }">
					<div class="w-100 d-flex justify-content-between align-items-center">
						<b-button
							variant="success"
							:disabled="modal.submitDisabled || modal.saving"
							@click="submitFaqForm()"
						>
							<b-spinner v-if="modal.saving" small class="mr-1"></b-spinner>
							{{ modal.saving ? 'Saving…' : 'Save' }}
						</b-button>
						<b-button variant="outline-secondary" size="sm" @click="cancel()">
						Cancel
					</b-button>
					</div>
				</template>
			</b-modal>

			<!-- Table -->
			<b-table
				bordered
				hover
				responsive
				:items="faqs"
				:busy="isBusy"
				:fields="fields"
				:sort-by.sync="sortBy"
				:sort-desc.sync="sortDesc"
				sort-icon-left
				show-empty
				@row-clicked="faqEdit"
			>
				<template #table-busy>
					<div class="text-center py-4">
						<b-spinner variant="secondary" class="mb-2"></b-spinner>
						<div class="text-muted small">Loading FAQs…</div>
					</div>
				</template>

				<template #empty>
					<div class="empty-state">
						<i class="fas fa-question-circle empty-icon"></i>
						<p class="empty-text">No FAQs yet.</p>
						<b-button class="btn-faq-add" size="sm" @click="faqAdd">
							<i class="fas fa-plus mr-1"></i> Add your first FAQ
						</b-button>
					</div>
				</template>

				<template #cell(question)="data">
					<span class="question-cell">{{ data.item.question }}</span>
				</template>

				<template #cell(answer)="data">
					<text-clamp :html="data.item.answer" />
				</template>

				<template #cell(status)="data">
					<b-badge :variant="statusVariant(data.item.status)" class="status-badge">
						{{ statusLabel(data.item.status) }}
					</b-badge>
				</template>

				<template #cell(actions)="data">
					<div class="d-flex" style="gap: 4px;">
						<b-button
							size="sm"
							variant="outline-secondary"
							class="action-btn"
							@click.stop="faqEdit(data.item)"
						>
							<i class="fas fa-pen"></i>
						</b-button>
						<b-button
							size="sm"
							variant="outline-danger"
							class="action-btn"
							@click.stop="confirmDelete(data.item)"
						>
							<i class="fas fa-trash-alt"></i>
						</b-button>
					</div>
				</template>
			</b-table>
		</div>
	</div>
</template>

<script>
import axios from '@/axios';
import BackBtn from '@/components/BackBtn.vue';
import { VueEditor } from 'vue2-editor';
import TextClamp from '@/components/programs/TextClamp.vue';

const emptyFaq = (sort) => ({
	id: null,
	question: '',
	answer: '',
	status: 1,
	sort: sort || 1,
});

export default {
	name: 'FaqAdmin',
	components: { BackBtn, VueEditor, TextClamp },
	data() {
		return {
			faqs: [],
			isBusy: true,
			sortBy: 'sort',
			sortDesc: false,
			totalRows: 0,
			fields: [
				{ key: 'sort', sortable: true, label: '#', thStyle: 'width: 60px; text-align: center;', tdClass: 'text-center text-muted' },
				{ key: 'question', sortable: true, label: 'Question' },
				{ key: 'answer', sortable: false, label: 'Answer preview' },
				{ key: 'status', sortable: true, label: 'Status', thStyle: 'width: 90px; text-align: center;', tdClass: 'text-center' },
				{ key: 'actions', sortable: false, label: '', thStyle: 'width: 90px;', tdClass: 'text-center' },
			],
			statuses: [
				{ value: 1, text: 'Active' },
				{ value: 0, text: 'Draft' },
			],
			modal: {
				title: '',
				submitDisabled: true,
				showErrors: false,
				saving: false,
				faq: emptyFaq(),
				errors: { process: '' },
			},
			editorToolbar: [
				['bold', 'italic', 'underline', 'strike'],
				[{ list: 'ordered' }, { list: 'bullet' }],
				[{ header: [2, 3, false] }],
				['link', 'blockquote'],
				['clean'],
			],
		};
	},
	computed: {
		user_level() {
			return this.$store.getters.profileGet.level;
		},
		hasAnswerContent() {
			return this.modal.faq.answer?.replace(/<[^>]*>/g, '').trim().length;
		},
	},
	methods: {
		statusLabel(val) {
			const s = this.statuses.find(s => s.value === parseInt(val));
			return s ? s.text : val;
		},
		statusVariant(val) {
			const map = { 1: 'success', 2: 'warning', 0: 'danger' };
			return map[parseInt(val)] || 'secondary';
		},
		validate() {
			this.modal.submitDisabled = !(this.modal.faq.question && this.hasAnswerContent);
		},
		faqAdd() {
			this.modal.faq = emptyFaq(this.faqs.length + 1);
			this.modal.title = 'Add FAQ';
			this.modal.errors.process = '';
			this.modal.showErrors = false;
			this.modal.saving = false;
			this.modal.submitDisabled = true;
			this.$bvModal.show('modal-faq');
		},
		faqEdit(item) {
			this.modal.faq = { ...item };
			this.modal.title = 'Edit FAQ';
			this.modal.errors.process = '';
			this.modal.showErrors = false;
			this.modal.saving = false;
			this.validate();
			this.$bvModal.show('modal-faq');
		},
		confirmDelete(item) {
			this.$bvModal
				.msgBoxConfirm('Are you sure you want to delete this FAQ? This cannot be undone.', {
					title: 'Delete FAQ',
					okVariant: 'danger',
					okTitle: 'Yes, delete',
					cancelTitle: 'Cancel',
					centered: true,
				})
				.then(confirmed => {
					if (!confirmed) return;
					this.modal.saving = true;
					this.modal.errors.process = '';
					axios({ method: 'delete', url: `admin/faqs/${item.id}` })
						.then(response => {
							this.modal.saving = false;
							if (response.data.status) {
                this.faqsGet()
								this.$refs['modal-faq'].hide();
								this.$toast.success('FAQ deleted.', { position: 'top-right', duration: 3000 });
							} else {
								this.modal.errors.process = response.data.message || 'Delete failed.';
							}
						})
						.catch(() => {
							this.modal.saving = false;
							this.modal.errors.process = 'Request failed. Please try again.';
						});
				})
				.catch(err => console.log(err));
		},
		submitFaqForm() {
			this.modal.showErrors = true;
			if (!this.modal.faq.question || !this.hasAnswerContent) {
				this.validate();
				return;
			}

			this.modal.saving = true;
			this.modal.errors.process = '';

			const { id, ...faqData } = this.modal.faq;
			const isEdit = !!id;

			axios({
				method: isEdit ? 'put' : 'post',
				url: isEdit ? `admin/faqs/${id}` : 'admin/faqs',
				data: faqData,
			})
				.then(response => {
					this.modal.saving = false;
					if (response.data.status) {
            this.faqsGet()
						this.$refs['modal-faq'].hide();
						this.$toast.success('FAQ saved successfully.', { position: 'top-right', duration: 3000 });
					} else {
						this.modal.errors.process = response.data.message || 'An error occurred.';
					}
				})
				.catch(() => {
					this.modal.saving = false;
					this.modal.errors.process = 'Request failed. Please try again.';
				});
		},
		faqsGet() {
			axios({ url: 'admin/faqs' })
				.then(response => {
					this.isBusy = false;
					this.faqs = response.data;
				})
				.catch(() => {
					this.isBusy = false;
					this.faqs = [];
				});
		},
	},
	mounted() {
		this.faqsGet();
	},
};
</script>

<!-- Unscoped: targets Bootstrap-Vue portal elements and dynamic table classes -->
<style>
.faq-modal-header {
	background-color: #d6b674;
	color: #fff;
}
.faq-modal-header .modal-title { font-weight: 700; }
.faq-modal-header .close { color: #fff; opacity: 0.8; }
.faq-modal-header .close:hover { opacity: 1; }

#faq_admin_section .b-table tbody tr { cursor: pointer; }
</style>

<style scoped>
.page-header {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 14px;
	position: relative;
	padding: 0 52px;
}
.page-header h2 {
	min-width: 0;
	text-align: center;
	word-break: break-word;
}
.gold { color: #d6b674; }
.faq-count {
	font-size: 0.6em;
	font-weight: 400;
	color: #888;
	vertical-align: middle;
}

.btn-faq-add {
	background-color: #d6b674;
	border-color: #c4a55a;
	color: #fff;
	font-weight: 600;
}
.btn-faq-add:hover,
.btn-faq-add:focus {
	background-color: #c4a55a;
	border-color: #b8963f;
	color: #fff;
}

.editor-wrap { border-radius: 4px; }
.editor-invalid { outline: 2px solid #dc3545; border-radius: 4px; }

.question-cell { font-weight: 500; color: #333; }
.status-badge { font-size: 0.8rem; }
.action-btn { padding: 2px 8px; }

.empty-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 48px 16px;
	color: #aaa;
}
.empty-icon {
	font-size: 3rem;
	margin-bottom: 12px;
	color: #d6b674;
	opacity: 0.6;
}
.empty-text {
	margin-bottom: 12px;
	font-size: 1rem;
}
</style>
